import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Camera, CameraOff, Check, X, AlertTriangle, Search, RotateCcw, ArrowLeft, Loader2 } from 'lucide-react';

import { BACKEND_URL } from '../lib/api';

const ScannerPage = () => {
  // When mounted at /associate/events/:eventId/scanner the route supplies
  // an eventId; at the legacy /scanner this is undefined and the scanner
  // runs unscoped (admin / owner / org-wide associate use-case).
  const { eventId } = useParams();
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [undoing, setUndoing] = useState(false);
  const [accessChecked, setAccessChecked] = useState(!eventId);
  const [accessDenied, setAccessDenied] = useState(false);
  const [eventInfo, setEventInfo] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  // Pre-flight permission check when the scanner is mounted at the
  // event-scoped route. We gate the camera and validation UI behind this.
  useEffect(() => {
    let active = true;
    if (!eventId) return undefined;
    (async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/associate/events/${encodeURIComponent(eventId)}/access`,
          { withCredentials: true },
        );
        if (!active) return;
        const perms = res.data?.permissions || {};
        if (!perms.can_scan_tickets) {
          setAccessDenied(true);
        } else {
          setEventInfo(res.data?.event);
        }
      } catch (e) {
        if (!active) return;
        // 403 / 404 both surface as "no access" so the page never silently
        // bounces to the homepage.
        setAccessDenied(true);
      } finally {
        if (active) setAccessChecked(true);
      }
    })();
    return () => { active = false; };
  }, [eventId]);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const startScanner = async () => {
    try {
      const html5QrCode = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
        },
        (decodedText) => {
          handleScan(decodedText);
          stopScanner();
        },
        () => {}
      );
      setScanning(true);
    } catch (err) {
      console.error('Scanner error:', err);
      toast.error('Could not access camera. Please check permissions or use manual entry.');
    }
  };

  const stopScanner = async () => {
    try {
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      }
    } catch (err) {
      // Ignore errors on cleanup
    }
    setScanning(false);
  };

  const handleScan = async (qrData) => {
    if (loading) return;
    setLoading(true);
    setResult(null);

    try {
      const body = { qr_data: qrData };
      // Scope server-side validation to this event so a stray ticket from
      // another event can never be checked in via this scanner.
      if (eventId) body.event_id = eventId;
      const response = await axios.post(
        `${BACKEND_URL}/api/tickets/validate`,
        body,
        { withCredentials: true }
      );
      setResult(response.data);

      if (response.data.valid && !response.data.already_checked_in) {
        toast.success('Check-in successful!');
      } else if (response.data.already_checked_in) {
        toast.error('Already checked in!');
      } else {
        toast.error(response.data.message || 'Invalid ticket');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Validation failed';
      toast.error(msg);
      setResult({ valid: false, message: msg, ticket: null });
    } finally {
      setLoading(false);
    }
  };

  const handleManualValidation = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) {
      toast.error('Please enter a ticket code');
      return;
    }
    handleScan(manualCode.trim());
  };

  const resetScanner = () => {
    setResult(null);
    setManualCode('');
  };

  const handleUndoCheckin = async () => {
    const code = result?.ticket?.ticket_code;
    if (!code) return;
    if (!window.confirm('Undo this check-in? The ticket will be scannable again.')) return;
    setUndoing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/tickets/${code}/undo-checkin`,
        {},
        { withCredentials: true },
      );
      toast.success('Check-in reversed. Ticket can be re-scanned.');
      // Reset and let the organiser scan again
      setResult(null);
      setManualCode('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not undo check-in.');
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {/* Pre-flight gates when scanner is mounted at the event-scoped route */}
      {eventId && !accessChecked && (
        <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="scanner-loading-access">
          <Loader2 className="w-8 h-8 animate-spin text-[#1F3D2B] mx-auto" />
          <p className="text-[#4A5D50] mt-4 text-sm">Checking event access…</p>
        </div>
      )}
      {eventId && accessChecked && accessDenied && (
        <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="scanner-access-denied">
          <div className="bg-white border border-stone-200 rounded-2xl shadow-sm px-8 py-10">
            <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-rose-500" />
            </div>
            <h1 className="font-serif text-2xl text-[#1F3D2B] mb-2">Permission denied</h1>
            <p className="text-stone-600 mb-6">
              You do not have permission to scan tickets for this event.
            </p>
            <Link
              to="/associate/events"
              className="inline-block bg-[#1F3D2B] text-white px-6 py-3 rounded-full font-medium"
            >
              Back to my events
            </Link>
          </div>
        </div>
      )}

      {(!eventId || (accessChecked && !accessDenied)) && (
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-12">

        {/* Event-scoped back link + event chip */}
        {eventId && eventInfo && (
          <div className="mb-6" data-testid="scanner-event-context">
            <Link
              to={`/associate/events/${eventId}/tools`}
              className="text-xs text-[#1F3D2B] hover:underline inline-flex items-center gap-1 mb-3"
              data-testid="scanner-back-to-tools"
            >
              <ArrowLeft className="w-3 h-3" /> Back to event tools
            </Link>
            <div className="bg-white border border-[#E5E9E6] rounded-xl px-4 py-3">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A27A] mb-0.5">Scanning for</p>
              <p className="font-serif text-base text-[#1F3D2B]" data-testid="scanner-event-title">{eventInfo.title}</p>
              <p className="text-xs text-[#4A5D50]">
                {eventInfo.date}{eventInfo.time ? ` · ${eventInfo.time}` : ''}{eventInfo.location ? ` · ${eventInfo.location}` : ''}
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <h1
            className="text-4xl font-bold text-[#112318] mb-2"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="scanner-title"
          >
            Ticket Scanner
          </h1>
          <p className="text-[#4A5D50] text-sm">
            Scan QR codes or enter ticket codes to validate entry
          </p>
        </div>

        {/* Result Card */}
        {result && (
          <div
            className={`rounded-2xl p-6 mb-6 border-2 ${
              result.valid && !result.already_checked_in
                ? 'bg-[#F0F7F2] border-[#4E7C59]'
                : result.already_checked_in
                ? 'bg-[#FFFBF0] border-[#C9A27A]'
                : 'bg-[#FFF5F5] border-[#D9534F]'
            }`}
            data-testid="scan-result"
          >
            {/* Status Icon */}
            <div className="flex items-center gap-3 mb-4">
              {result.valid && !result.already_checked_in ? (
                <div className="w-12 h-12 bg-[#1F3D2B] rounded-full flex items-center justify-center">
                  <Check className="w-6 h-6 text-white" />
                </div>
              ) : result.already_checked_in ? (
                <div className="w-12 h-12 bg-[#C9A27A] rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
              ) : (
                <div className="w-12 h-12 bg-[#D9534F] rounded-full flex items-center justify-center">
                  <X className="w-6 h-6 text-white" />
                </div>
              )}
              <div>
                <h3 className="text-lg font-bold text-[#112318]">
                  {result.valid && !result.already_checked_in
                    ? 'Valid Ticket'
                    : result.already_checked_in
                    ? 'Already Checked In'
                    : 'Invalid Ticket'}
                </h3>
                <p className="text-sm text-[#4A5D50]">{result.message}</p>
              </div>
            </div>

            {/* Ticket Details */}
            {result.ticket && (
              <div className="bg-white rounded-xl p-4 space-y-3 border border-[#E5E9E6]">
                <div>
                  <p className="text-xs text-[#4A5D50] uppercase tracking-wider">Event</p>
                  <p className="text-sm font-semibold text-[#112318]" data-testid="scan-event-title">{result.ticket.event_title}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-[#4A5D50] uppercase tracking-wider">Date</p>
                    <p className="text-sm font-medium text-[#112318]">{result.ticket.event_date}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#4A5D50] uppercase tracking-wider">Time</p>
                    <p className="text-sm font-medium text-[#112318]">{result.ticket.event_time}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-[#4A5D50] uppercase tracking-wider">Ticket Type</p>
                    <p className="text-sm font-medium text-[#112318]">{result.ticket.ticket_type}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#4A5D50] uppercase tracking-wider">Ticket ID</p>
                    <p className="text-sm font-mono font-medium text-[#1F3D2B]">{result.ticket.ticket_code}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-[#4A5D50] uppercase tracking-wider">Attendee</p>
                    <p className="text-sm font-medium text-[#112318]">{result.ticket.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#4A5D50] uppercase tracking-wider">Location</p>
                    <p className="text-sm font-medium text-[#112318]">{result.ticket.event_location}</p>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={resetScanner}
              className="w-full mt-4 bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full"
              data-testid="scan-next-button"
            >
              Scan Next Ticket
            </Button>
            {result.valid && result.ticket && (
              <Button
                onClick={handleUndoCheckin}
                disabled={undoing}
                variant="outline"
                className="w-full mt-2 border-[#B04D4D] text-[#B04D4D] hover:bg-[#B04D4D]/10 rounded-full"
                data-testid="undo-checkin-button"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {undoing ? 'Reversing…' : 'Undo Check-In'}
              </Button>
            )}
          </div>
        )}

        {/* Scanner */}
        {!result && (
          <>
            {/* Camera Scanner */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E5E9E6] mb-6">
              <div
                id="qr-reader"
                ref={scannerRef}
                className="w-full"
                style={{ minHeight: scanning ? '300px' : '0px' }}
              />

              {!scanning && (
                <div className="p-8 text-center">
                  <Camera className="w-12 h-12 text-[#4A5D50] mx-auto mb-4" />
                  <p className="text-[#4A5D50] text-sm mb-4">
                    Use your device camera to scan ticket QR codes
                  </p>
                  <Button
                    onClick={startScanner}
                    className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full px-8"
                    data-testid="start-scanner-button"
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    Open Camera
                  </Button>
                </div>
              )}

              {scanning && (
                <div className="p-4 text-center">
                  <Button
                    onClick={stopScanner}
                    variant="outline"
                    className="rounded-full"
                    data-testid="stop-scanner-button"
                  >
                    <CameraOff className="w-4 h-4 mr-2" />
                    Stop Camera
                  </Button>
                </div>
              )}
            </div>

            {/* OR Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#E5E9E6]" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-[#FAFAF7] text-[#4A5D50]">OR ENTER CODE MANUALLY</span>
              </div>
            </div>

            {/* Manual Entry */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#E5E9E6]">
              <form onSubmit={handleManualValidation} className="flex gap-3">
                <Input
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                  placeholder="Enter ticket code (e.g. 98C53537FF6)"
                  className="flex-1 font-mono"
                  data-testid="manual-code-input"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full px-6"
                  data-testid="manual-submit-button"
                >
                  {loading ? '...' : <Search className="w-5 h-5" />}
                </Button>
              </form>
            </div>
          </>
        )}

        {/* Instructions */}
        <div className="mt-8 text-center text-xs text-[#4A5D50] space-y-1">
          <p>Point your camera at the QR code on the guest's ticket</p>
          <p>Green = Valid entry &middot; Yellow = Already scanned &middot; Red = Invalid</p>
        </div>
      </div>
      )}
    </div>
  );
};

export default ScannerPage;
